import React, { useEffect, useRef, useState } from "react";
import { FaTrash } from "react-icons/fa";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";
import { isAdminAuthLevel } from "../../utils/opsLeadAttachments";

const FileUploadAddLead = ({
  setFileNames,
  fileNames,
  SelectedCategory,
  setSelectedCategory,
  projectName = "",
}) => {
  const auth =
    typeof localStorage !== "undefined" ? localStorage.getItem("auth") : "";
  const isAdmin = isAdminAuthLevel(auth);
  const normalizedAuth = String(auth || "").trim().toUpperCase();
  const isDirector = normalizedAuth === "L1";
  const canShowAttachmentControls = !isAdmin;
  const allowedCategoriesForUser = isDirector
    ? ["Quote", "Purchase Order", "Contract"]
    : ["Quote", "Purchase Order", "Contract", "Communication", "Miscellaneous"];

  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [editfetchedfiles, seteditFetchedFiles] = useState([]);

  const dropdownRefProbability = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefProbability.current &&
        !dropdownRefProbability.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const downloadFile = (file) => {
    if (!file) return;
    
    // Handle existing files from server (have filepathurl)
    if (file.filepathurl) {
      const fileUrl = file.filepathurl;
      const fileName = file.filename || file.name || fileUrl.split("/").pop();
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName);
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      enqueueSnackbar("File download started", {
        variant: "success",
      });
      return;
    }
    
    // Handle new files (File objects)
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name; // use file name from File object
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    enqueueSnackbar("File Downloaded successfully", {
      variant: "success",
    });
  };

  const handleDownload = (file) => {
    downloadFile(file);
  };

  const [existingNames, setExistingNames] = useState([]);

  useEffect(() => {
    setExistingNames([
      ...fileNames,
      ...editfetchedfiles,
    ].map((f) => f.name ?? f.fileName));
  }, [fileNames, editfetchedfiles]);

  const filesVisibleToUser = isAdmin
    ? []
    : (Array.isArray(fileNames) ? fileNames : []).filter((f) =>
        allowedCategoriesForUser.includes(String(f?.category || f?.selectedCategory || "").trim())
      );


  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const allowedFormats = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel", // .xls
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    ];

    if (!SelectedCategory) {
      enqueueSnackbar("Please select a category first", { variant: "error" });
      return;
    }

    const validFiles = [];
    selectedFiles.forEach((file) => {
      if (!allowedFormats.includes(file.type)) {
        enqueueSnackbar("Only PDF and DOC files are allowed", {
          variant: "error",
        });
        return;
      }
      if (existingNames.includes(file.name)) {
        enqueueSnackbar("File is already uploaded.", { variant: "error" });
        return;
      }
      const fileWithCategory = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
      fileWithCategory.selectedCategory = SelectedCategory;
      validFiles.push(fileWithCategory);
    });

    if (validFiles.length === 0) return;
    setFileNames((prev) => [...prev, ...validFiles]);
    seteditFetchedFiles((prev) => [
      ...prev,
      ...validFiles.map((f) => ({
        fileName: f.name,
        selectedCategory: f.selectedCategory,
        fileContent: "",
        isNew: true,
      })),
    ]);
    enqueueSnackbar("File uploaded successfully", { variant: "success" });
    setSelectedCategory("");
    e.target.value = null;
  };

  const handleFileDelete = (file) => {
    const fileName = file.fileName || file.name;

    setFileNames((prev) =>
      prev.filter((f) => (f.name ?? f.fileName) !== fileName)
    );

    seteditFetchedFiles((prev) => {
      const updated = prev.filter((f) => f.fileName !== fileName);
      return updated.length === 0 ? [] : updated;
    });

    enqueueSnackbar("File deleted successfully", {
      variant: "success",
    });
  };


  return (
    <div>
      <div>
        <label className="block text-white" htmlFor="date">
          Upload file
        </label>
        <button
          onClick={() => setShowModal(true)}
          className="py-2 px-7  bg-black hover:text-white border hover:bg-slate-800 border-black overflow-hidden shadow-md text-white rounded-lg"
        >
          Upload File
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 flex justify-center items-center z-50 bg-opacity-50 bg-black">
          <div className="w-[50%] bg-white shadow-lg rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              {isAdmin ? (
                <div className="flex align-middle items-center gap-2">
                  <h2 className="text-lg font-bold">Attachments </h2>
                  <p>
                    ({"Project Name : "}
                    {projectName})
                  </p>
                </div>
              ) : (
                <h2 className="text-lg font-bold">Attachments</h2>
              )}
              <button
                className="text-gray-500 hover:text-black"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
            {canShowAttachmentControls && (
              <div className="flex gap-2">
                <div className="relative bg-white">
                  <div
                    ref={dropdownRefProbability}
                    className=" flex  p-0  rounded-xl align-middle text-[12px]"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setShowCategoryDropdown(!showCategoryDropdown)
                      }
                      className="border relative w-24 h-8 border-gray-400 text-nowrap rounded-md p-2 shadow-md overflow-hidden bg-gray-200 outline-none flex items-center justify-between"
                    >
                      <p className="text-left text-xs truncate">
                        {SelectedCategory ? SelectedCategory : "Category"}
                      </p>
                      <ExpandMoreIcon
                        color="white"
                        className="cursor-pointer bg-gray-200 flex-shrink-0"
                      />
                    </button>

                    {showCategoryDropdown && (
                      <div
                        style={{ zIndex: 9999 }}
                        className="absolute top-full left-0 w-40 p-1 cursor-pointer max-h-96 overflow-y-auto bg-white text-sm border border-gray-300 shadow-lg flex flex-col gap-1"
                      >
                        {allowedCategoriesForUser.map((cat) => (
                          <div
                            key={cat}
                            className="p-1 hover:bg-gray-200 cursor-pointer"
                            onClick={() => {
                              setSelectedCategory(cat);
                              setShowCategoryDropdown(!showCategoryDropdown);
                            }}
                          >
                            {cat}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="file"
                    multiple
                    id={`fileUpload`}
                    className="hidden"
                    onChange={(e) => handleFileChange(e)}
                    disabled={!SelectedCategory}
                  />

                  <label
                    htmlFor="fileUpload"
                    className={`text-xs p-2 w-24 h-8 text-center rounded-md flex items-center justify-center ${!SelectedCategory
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-gray-800 text-white hover:text-black hover:bg-gray-300 cursor-pointer"
                      }`}
                  >
                    Upload File
                  </label>

                  <p className="truncate text-xs pl-1">
                    {fileNames?.name || "No file selected"}
                  </p>
                  <div className="flex gap-2 text-red-500 text-[12px] pl-3">
                    <p> only .pdf, .xlsx and .docx allowed</p>
                  </div>
                </div>
              </div>
            )}
            <div className="text-[13px]">
              {isAdmin ? (
                <div className="w-full mt-4 text-left border-collapse text-[13px]">
                  <div>
                    <div className="border-b flex w-full">
                      <div className="py-2 px-4 w-[15%]">Sr. No.</div>
                      <div className="py-2 px-4 w-[40%]">File Name</div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Category
                      </div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Download
                      </div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Delete
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="py-4 px-4 text-center text-gray-500">
                      No attachments available.
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div className="border-b flex w-full">
                      <div className="py-2 px-4 w-[15%]">Sr. No.</div>
                      <div className="py-2 px-4 w-[40%]">File Name</div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Category
                      </div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Download
                      </div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Delete
                      </div>
                    </div>
                  </div>
                  <div>
                    {filesVisibleToUser.map((file, index) => (
                      <div
                        className="border-b flex w-full"
                        key={file.id ?? index}
                      >
                        <div className="py-2 px-4 w-[15%]">{index + 1}</div>
                        <div className="py-2 px-4 w-[40%] truncate overflow-hidden">
                          {file.filename || file.name}
                        </div>
                        <div className="py-2 px-4 w-[15%] text-center truncate">
                          {file.category || file.selectedCategory}
                        </div>
                        <div className="py-2 px-4 text-center w-[15%]">
                          <button
                            onClick={() => handleDownload(file)}
                            className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-700"
                          >
                            <CloudDownloadIcon />
                          </button>
                        </div>
                        <div className="py-2 px-4 text-center w-[15%]">
                          <button
                            onClick={() => handleFileDelete(file)}
                            className="bg-red-500 text-white px-2 py-2 rounded hover:bg-red-700"
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      )
      }
    </div >
  );
};

export default FileUploadAddLead;
